type BgmTrack = {
    id: string,
    label: string,
    src: string,
};

type BgmRepeatMode = "one" | "all";

const DEFAULT_BGM_TRACK_ID = "noah-night";
const DEFAULT_BGM_TRACK_SRC = "/bgm/noah-night.mp3";

const FALLBACK_BGM_TRACKS: BgmTrack[] = [
    {
        id: "noah-night",
        label: "深夜の避難所",
        src: "/bgm/noah-night.mp3",
    },
];

let bgmTracks: BgmTrack[] = FALLBACK_BGM_TRACKS;

const BGM_AUDIO_ID = "noah-bgm-audio";
const BGM_PANEL_ID = "noah-bgm-panel";
const BGM_TOGGLE_ID = "noah-bgm-toggle";
const BGM_SELECT_ID = "noah-bgm-select";
const BGM_BUTTON_ID = "noah-bgm-play-toggle";
const BGM_REPEAT_BUTTON_ID = "noah-bgm-repeat";
const BGM_VOLUME_ID = "noah-bgm-volume";
const BGM_MESSAGE_ID = "noah-bgm-message";

const STORAGE_ENABLED = "noahBgmEnabled";
const STORAGE_VOLUME = "noahBgmVolume";
const STORAGE_TRACK_ID = "noahBgmTrackId";
const STORAGE_REPEAT_MODE = "noahBgmRepeatMode";

function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, character => {
        switch (character) {
            case "&": return "&amp;";
            case "<": return "&lt;";
            case ">": return "&gt;";
            case '"': return "&quot;";
            case "'": return "&#039;";
            default: return character;
        }
    });
}

async function loadBgmTracks(): Promise<BgmTrack[]> {
    try {
        const response = await fetch("/api/bgm-tracks", {
            credentials: "same-origin",
        });

        const data = await response.json();

        if (response.ok && data.ok && Array.isArray(data.tracks) && data.tracks.length > 0) {
            const tracks = data.tracks.filter((track: Partial<BgmTrack>) =>
                typeof track.id === "string" &&
                typeof track.label === "string" &&
                typeof track.src === "string"
            ) as BgmTrack[];

            if (tracks.length > 0) {
                return tracks;
            }
        }
    }
    catch {
        // fallback below
    }

    return FALLBACK_BGM_TRACKS;
}

function getStoredVolume(): number {
    const storedValue = localStorage.getItem(STORAGE_VOLUME);
    const parsedValue = storedValue === null ? 0.35 : Number(storedValue);

    if (Number.isNaN(parsedValue)) {
        return 0.35;
    }

    return Math.min(1, Math.max(0, parsedValue));
}

function getDefaultTrack(): BgmTrack {
    return bgmTracks.find(track => track.id === DEFAULT_BGM_TRACK_ID)
        || bgmTracks.find(track => track.src === DEFAULT_BGM_TRACK_SRC)
        || bgmTracks[0]
        || FALLBACK_BGM_TRACKS[0];
}

function getSelectedTrack(): BgmTrack {
    const storedTrackId = localStorage.getItem(STORAGE_TRACK_ID);
    const storedTrack = bgmTracks.find(track => track.id === storedTrackId);

    return storedTrack || getDefaultTrack();
}

function getStoredRepeatMode(): BgmRepeatMode {
    const storedValue = localStorage.getItem(STORAGE_REPEAT_MODE);
    return storedValue === "one" ? "one" : "all";
}

function setStoredRepeatMode(repeatMode: BgmRepeatMode): void {
    localStorage.setItem(STORAGE_REPEAT_MODE, repeatMode);
}

function getNextRepeatMode(repeatMode: BgmRepeatMode): BgmRepeatMode {
    return repeatMode === "one" ? "all" : "one";
}

function setMessage(message: string): void {
    const messageElement = document.getElementById(BGM_MESSAGE_ID);
    if (messageElement) {
        messageElement.textContent = message;
    }
}

function updatePlayButtonText(audio: HTMLAudioElement): void {
    const button = document.getElementById(BGM_BUTTON_ID) as HTMLButtonElement | null;
    if (!button) return;
    button.textContent = audio.paused ? "BGM OFF" : "BGM ON";
}

function updateRepeatButtonText(repeatMode: BgmRepeatMode): void {
    const button = document.getElementById(BGM_REPEAT_BUTTON_ID) as HTMLButtonElement | null;
    if (!button) return;

    if (repeatMode === "one") {
        button.textContent = "↻ 1曲";
        button.title = "1曲リピート";
    }
    else {
        button.textContent = "↻ 全曲";
        button.title = "全曲リピート";
    }
}

function applyTrack(audio: HTMLAudioElement, track: BgmTrack): void {
    if (audio.getAttribute("src") === track.src) {
        localStorage.setItem(STORAGE_TRACK_ID, track.id);
        return;
    }

    audio.src = track.src;
    audio.load();
    localStorage.setItem(STORAGE_TRACK_ID, track.id);
}

function getCurrentTrackIndex(audio: HTMLAudioElement, select: HTMLSelectElement): number {
    const selectedIndex = bgmTracks.findIndex(track => track.id === select.value);
    if (selectedIndex >= 0) return selectedIndex;

    const audioSrc = audio.getAttribute("src") || "";
    const audioIndex = bgmTracks.findIndex(track => track.src === audioSrc);
    return audioIndex >= 0 ? audioIndex : 0;
}

async function playBgm(audio: HTMLAudioElement): Promise<void> {
    try {
        await audio.play();
        localStorage.setItem(STORAGE_ENABLED, "true");
        setMessage("");
    }
    catch {
        localStorage.setItem(STORAGE_ENABLED, "false");
        setMessage("BGMを再生できませんでした。");
    }
    updatePlayButtonText(audio);
}

function pauseBgm(audio: HTMLAudioElement): void {
    audio.pause();
    localStorage.setItem(STORAGE_ENABLED, "false");
    setMessage("");
    updatePlayButtonText(audio);
}

function createTrackOptionsHtml(selectedTrack: BgmTrack): string {
    return bgmTracks.map(track => {
        const selected = track.id === selectedTrack.id ? "selected" : "";
        return `<option value="${escapeHtml(track.id)}" ${selected}>${escapeHtml(track.label)}</option>`;
    }).join("");
}

function createBgmPanel(): HTMLElement {
    const panel = document.createElement("div");
    panel.id = BGM_PANEL_ID;
    panel.hidden = true;

    const selectedTrack = getSelectedTrack();
    const repeatMode = getStoredRepeatMode();

    panel.innerHTML = `
        <audio id="${BGM_AUDIO_ID}" src="${escapeHtml(selectedTrack.src)}" preload="metadata"></audio>
        <select id="${BGM_SELECT_ID}" title="BGM選択">
            ${createTrackOptionsHtml(selectedTrack)}
        </select>
        <button id="${BGM_BUTTON_ID}" type="button">BGM OFF</button>
        <button id="${BGM_REPEAT_BUTTON_ID}" type="button">${repeatMode === "one" ? "↻ 1曲" : "↻ 全曲"}</button>
        <label id="noah-bgm-volume-label">
            音量
            <input id="${BGM_VOLUME_ID}" type="range" min="0" max="100" step="1" />
        </label>
        <div id="${BGM_MESSAGE_ID}"></div>
    `;

    return panel;
}

function createBgmToggleButton(): HTMLButtonElement {
    const button = document.createElement("button");
    button.id = BGM_TOGGLE_ID;
    button.type = "button";
    button.textContent = "♪";
    button.title = "BGM";
    return button;
}

function applyBgmStyle(): void {
    if (document.getElementById("noah-bgm-style")) return;

    const style = document.createElement("style");
    style.id = "noah-bgm-style";
    style.textContent = `
#${BGM_TOGGLE_ID} {
    min-width: 42px;
    cursor: pointer;
}

#${BGM_PANEL_ID} {
    position: fixed;
    right: 12px;
    bottom: 12px;
    z-index: 9999;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    max-width: min(420px, calc(100vw - 24px));
    padding: 8px 10px;
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 8px;
    background: rgba(0,0,0,0.72);
    color: #fff;
    box-sizing: border-box;
    font-size: 12px;
}

#${BGM_PANEL_ID}[hidden] {
    display: none !important;
}

#${BGM_SELECT_ID} {
    max-width: 170px;
}

#${BGM_BUTTON_ID},
#${BGM_REPEAT_BUTTON_ID} {
    cursor: pointer;
    white-space: nowrap;
}

#noah-bgm-volume-label {
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

#${BGM_VOLUME_ID} {
    width: 90px;
}

#${BGM_MESSAGE_ID} {
    max-width: 220px;
    line-height: 1.4;
}

@media screen and (max-width: 600px) {
    #${BGM_PANEL_ID} {
        right: 8px;
        bottom: 8px;
        max-width: calc(100vw - 16px);
    }

    #${BGM_SELECT_ID} {
        max-width: 140px;
    }

    #${BGM_VOLUME_ID} {
        width: 80px;
    }
}
`;
    document.head.appendChild(style);
}

function findShortcutContainer(): HTMLElement | null {
    const buttons = Array.from(document.querySelectorAll("button")) as HTMLButtonElement[];

    // まず #ｲﾍﾞﾝﾄ / #event を優先的に探す
    const target = buttons.find(button => {
        const text = (button.textContent || "").trim();
        return text === "#ｲﾍﾞﾝﾄ" || text === "#event";
    }) || buttons.find(button => {
        const text = (button.textContent || "").trim();
        return text === "#ﾘｽﾄ" || text === "#list";
    });

    return target?.parentElement as HTMLElement | null;
}

function appendToggleButton(toggleButton: HTMLButtonElement): boolean {
    if (document.getElementById(BGM_TOGGLE_ID)) {
        return true;
    }

    const container = findShortcutContainer();
    if (!container) return false;

    container.appendChild(toggleButton);
    return true;
}

function waitAndAppendToggleButton(toggleButton: HTMLButtonElement): void {
    if (appendToggleButton(toggleButton)) return;

    const observer = new MutationObserver(() => {
        if (appendToggleButton(toggleButton)) {
            observer.disconnect();
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
}

async function initBgmPlayerAsync(): Promise<void> {
    if (document.getElementById(BGM_PANEL_ID) || document.getElementById(BGM_TOGGLE_ID)) {
        return;
    }

    bgmTracks = await loadBgmTracks();
    applyBgmStyle();

    const panel = createBgmPanel();
    const toggleButton = createBgmToggleButton();

    const audio = panel.querySelector(`#${BGM_AUDIO_ID}`) as HTMLAudioElement;
    const select = panel.querySelector(`#${BGM_SELECT_ID}`) as HTMLSelectElement;
    const playButton = panel.querySelector(`#${BGM_BUTTON_ID}`) as HTMLButtonElement;
    const repeatButton = panel.querySelector(`#${BGM_REPEAT_BUTTON_ID}`) as HTMLButtonElement;
    const volume = panel.querySelector(`#${BGM_VOLUME_ID}`) as HTMLInputElement;

    let repeatMode = getStoredRepeatMode();

    const selectedTrack = getSelectedTrack();
    applyTrack(audio, selectedTrack);
    select.value = selectedTrack.id;

    const storedVolume = getStoredVolume();
    audio.volume = storedVolume;
    volume.value = String(Math.round(storedVolume * 100));

    // 初期状態は常にOFF
    localStorage.setItem(STORAGE_ENABLED, "false");
    updatePlayButtonText(audio);
    updateRepeatButtonText(repeatMode);

    toggleButton.addEventListener("click", () => {
        panel.hidden = !panel.hidden;
    });

    playButton.addEventListener("click", () => {
        if (audio.paused) {
            playBgm(audio);
        }
        else {
            pauseBgm(audio);
        }
    });

    repeatButton.addEventListener("click", () => {
        repeatMode = getNextRepeatMode(repeatMode);
        setStoredRepeatMode(repeatMode);
        updateRepeatButtonText(repeatMode);
    });

    select.addEventListener("change", () => {
        const nextTrack = bgmTracks.find(track => track.id === select.value) || getDefaultTrack();
        const shouldResume = !audio.paused;

        applyTrack(audio, nextTrack);
        setMessage("");

        if (shouldResume) {
            playBgm(audio);
        }
        else {
            updatePlayButtonText(audio);
        }
    });

    volume.addEventListener("input", () => {
        const volumeValue = Number(volume.value) / 100;
        audio.volume = volumeValue;
        localStorage.setItem(STORAGE_VOLUME, String(volumeValue));
    });

    audio.addEventListener("error", () => {
        setMessage("BGMファイルを読み込めませんでした。");
        updatePlayButtonText(audio);
    });

    audio.addEventListener("ended", () => {
        if (repeatMode === "one") {
            audio.currentTime = 0;
            playBgm(audio);
            return;
        }

        if (repeatMode === "all" && bgmTracks.length > 0) {
            const currentTrackIndex = getCurrentTrackIndex(audio, select);
            const nextTrack = bgmTracks[(currentTrackIndex + 1) % bgmTracks.length];

            applyTrack(audio, nextTrack);
            select.value = nextTrack.id;
            playBgm(audio);
            return;
        }

        updatePlayButtonText(audio);
    });

    audio.addEventListener("pause", () => updatePlayButtonText(audio));
    audio.addEventListener("play", () => updatePlayButtonText(audio));

    document.body.appendChild(panel);
    waitAndAppendToggleButton(toggleButton);
}

export function initBgmPlayer(): void {
    void initBgmPlayerAsync();
}
